// utils/dashboardExportHelper.js
import { exportPreviewToPDFByPages } from "./pdfHelper";

/**
 * Export dashboard data to PDF using the existing PDF pagination system
 */
export async function exportDashboardToPDF(dashboardData, siteId, range, filename = "dashboard-report.pdf") {
  // Create a hidden div with dashboard content using the same structure as PDFPreview
  const exportContainer = document.createElement("div");
  exportContainer.style.position = "absolute";
  exportContainer.style.left = "-9999px";
  exportContainer.style.top = "0";
  exportContainer.style.width = "794px";
  exportContainer.style.backgroundColor = "#ffffff";
  exportContainer.style.padding = "40px";
  exportContainer.style.fontFamily = "system-ui, -apple-system, sans-serif";
  exportContainer.style.fontSize = "14px";
  exportContainer.style.lineHeight = "1.5";
  exportContainer.style.color = "#000000";
  exportContainer.style.boxSizing = "border-box";

  // Build the dashboard content with proper PDF block structure
  const dashboardHTML = generateDashboardHTML(dashboardData, siteId, range);
  exportContainer.innerHTML = dashboardHTML;

  document.body.appendChild(exportContainer);

  try {
    // Use the existing PDF pagination system
    await exportPreviewToPDFByPages(exportContainer, filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
  } finally {
    document.body.removeChild(exportContainer);
  }
}


/**
 * Generate HTML content for PDF export with proper PDF block structure
 */
function generateDashboardHTML(dashboardData, siteId, range) {
  const currentDate = new Date().toLocaleDateString();
  const siteLabel = siteId === "all" ? "All Sites" : siteId;
  
  console.log('Dashboard Data for Export:', dashboardData);
  
  return `
    <div style="font-family: Arial, sans-serif; color: #000; padding: 40px;">
      <!-- Header Block -->
      <div data-pdf-block data-pdf-header style="text-align: center; margin-bottom: 40px; border-bottom: 2px solid #06135A; padding-bottom: 20px;">
        <h1 style="color: #06135A; font-size: 24px; margin: 0;">Dashboard Report</h1>
        <p style="color: #72777B; margin: 10px 0 0 0;">Generated on ${currentDate}</p>
        <p style="color: #72777B; margin: 5px 0 0 0;">Site: ${siteLabel} | Period: Last ${range} Days</p>
      </div>

      <!-- Status Overview Block -->
      <div data-pdf-block style="margin-bottom: 30px;">
        <h2 style="color: #06135A; font-size: 18px; margin-bottom: 20px;">Status Overview</h2>
        
        <div data-pdf-subblock style="margin-bottom: 20px;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px;">
              <h3 style="color: #06135A; font-size: 16px; margin: 0 0 10px 0;">Users</h3>
              ${generateUsersSection(dashboardData?.statusOverview?.users)}
            </div>
            <div style="border: 1px solid #ddd; padding: 15px; border-radius: 8px;">
              <h3 style="color: #06135A; font-size: 16px; margin: 0 0 10px 0;">Cases</h3>
              ${generateCasesSection(dashboardData?.statusOverview?.cases)}
            </div>
          </div>
        </div>
      </div>

      <!-- Case Performance Block -->
      <div data-pdf-block style="margin-bottom: 30px;">
        <h2 style="color: #06135A; font-size: 18px; margin-bottom: 20px;">Case Performance</h2>
        
        <div data-pdf-subblock style="border: 1px solid #ddd; padding: 15px; border-radius: 8px;">
          ${generateCasePerformanceTable(dashboardData?.casePerformance || [])}
        </div>
      </div>

      <!-- Case Comparison Block -->
      <div data-pdf-block style="margin-bottom: 30px;">
        <h2 style="color: #06135A; font-size: 18px; margin-bottom: 20px;">Case Comparison</h2>
        
        <div data-pdf-subblock style="border: 1px solid #ddd; padding: 15px; border-radius: 8px;">
          ${generateCaseComparisonTable(dashboardData?.caseComparison || [])}
        </div>
      </div>

      <!-- Findings Breakdown Block -->
      <div data-pdf-block style="margin-bottom: 30px;">
        <h2 style="color: #06135A; font-size: 18px; margin-bottom: 20px;">Findings Breakdown</h2>
        
        <div data-pdf-subblock style="border: 1px solid #ddd; padding: 15px; border-radius: 8px;">
          ${generateFindingsTable(dashboardData?.findingsBreakdown || [])}
        </div>
      </div>

      <!-- Single vs Multiple Cases Block -->
      <div data-pdf-block style="margin-bottom: 30px;">
        <h2 style="color: #06135A; font-size: 18px; margin-bottom: 20px;">Single vs Multiple Cases</h2>
        
        <div data-pdf-subblock style="border: 1px solid #ddd; padding: 15px; border-radius: 8px;">
          ${generateSingleVsMultipleTable(dashboardData?.singleVsMultipleCases || [])}
        </div>
      </div>
    </div>
  `;
}


/**
 * Generate users section HTML
 */
function generateUsersSection(usersData) {
  console.log('Users Data:', usersData);
  
  if (!usersData) {
    return '<p style="color: #72777B; text-align: center;">No users data available</p>';
  }
  
  // Handle the actual data structure
  const totalUsers = usersData.totalUsers || 0;
  const perSite = usersData.perSite || [];
  
  let html = `
    <div style="display: flex; justify-content: space-between; margin: 5px 0; padding: 5px 0; border-bottom: 1px solid #eee;">
      <span style="color: #72777B;">Total Users:</span>
      <strong style="color: #06135A;">${totalUsers}</strong>
    </div>
  `;
  
  if (perSite && perSite.length > 0) {
    html += '<div style="margin-top: 10px;"><strong style="color: #06135A;">Per Site:</strong></div>';
    perSite.forEach(site => {
      const siteName = site.siteName || 'Unknown Site';
      const userCount = site.users || 0;
      
      html += `
        <div style="display: flex; justify-content: space-between; margin: 3px 0; padding: 3px 0; padding-left: 15px;">
          <span style="color: #72777B;">${siteName}:</span>
          <span style="color: #06135A;">${userCount}</span>
        </div>
      `;
    });
  }
  
  return html;
}

/**
 * Generate cases section HTML
 */
function generateCasesSection(casesData) {
  console.log('Cases Data:', casesData);
  
  if (!casesData) {
    return '<p style="color: #72777B; text-align: center;">No cases data available</p>';
  }
  
  // Handle the actual data structure
  const totalCases = casesData.totalCases || 0;
  const perSite = casesData.perSite || [];
  
  let html = `
    <div style="display: flex; justify-content: space-between; margin: 5px 0; padding: 5px 0; border-bottom: 1px solid #eee;">
      <span style="color: #72777B;">Total Cases:</span>
      <strong style="color: #06135A;">${totalCases}</strong>
    </div>
  `;
  
  if (perSite && perSite.length > 0) {
    html += '<div style="margin-top: 10px;"><strong style="color: #06135A;">Per Site:</strong></div>';
    perSite.forEach(site => {
      const siteName = site.siteName || 'Unknown Site';
      const caseCount = site.cases || 0;
      
      html += `
        <div style="display: flex; justify-content: space-between; margin: 3px 0; padding: 3px 0; padding-left: 15px;">
          <span style="color: #72777B;">${siteName}:</span>
          <span style="color: #06135A;">${caseCount}</span>
        </div>
      `;
    });
  }
  
  return html;
}

/**
 * Generate case performance table
 */
function generateCasePerformanceTable(performanceData) {
  if (!performanceData || performanceData.length === 0) {
    return '<p style="color: #72777B; text-align: center;">No performance data available</p>';
  }

  return `
    <table style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr style="background-color: #f8f9fa;">
          <th style="padding: 10px; border: 1px solid #ddd;">Site Name</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Average Time from Case Entry to Signing</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Average Time from Case Opening to Signing</th>
        </tr>
      </thead>
      <tbody>
        ${performanceData.map(row => `
          <tr>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.siteName || '-'}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${formatTimeFromDays(row.avgEntryToSign)}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${formatTimeFromDays(row.avgOpenToSign)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

/**
 * Generate case comparison table
 */
function generateCaseComparisonTable(comparisonData) {
  if (!comparisonData || comparisonData.length === 0) {
    return '<p style="color: #72777B; text-align: center;">No comparison data available</p>';
  }

  return `
    <table style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr style="background-color: #f8f9fa;">
          <th style="padding: 10px; border: 1px solid #ddd;">Site Name</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Total</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Entered</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Opened</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Signed</th>
        </tr>
      </thead>
      <tbody>
        ${comparisonData.map(row => `
          <tr>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.siteName || '-'}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.total || 0}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.entered || 0}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.opened || 0}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.signed || 0}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

/**
 * Generate findings breakdown table
 */
function generateFindingsTable(findingsData) {
  if (!findingsData || findingsData.length === 0) {
    return '<p style="color: #72777B; text-align: center;">No findings data available</p>';
  }

  return `
    <table style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr style="background-color: #f8f9fa;">
          <th style="padding: 10px; border: 1px solid #ddd;">Site Name</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Algorithm Findings</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Radiologist Findings</th>
        </tr>
      </thead>
      <tbody>
        ${findingsData.map(row => `
          <tr>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.siteName || '-'}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.algo || 0}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.radiologist || 0}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

/**
 * Generate single vs multiple cases table
 */
function generateSingleVsMultipleTable(singleVsMultipleData) {
  if (!singleVsMultipleData || singleVsMultipleData.length === 0) {
    return '<p style="color: #72777B; text-align: center;">No single vs multiple data available</p>';
  }

  return `
    <table style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr style="background-color: #f8f9fa;">
          <th style="padding: 10px; border: 1px solid #ddd;">Site Name</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Single Cases</th>
          <th style="padding: 10px; border: 1px solid #ddd;">Multiple Cases</th>
        </tr>
      </thead>
      <tbody>
        ${singleVsMultipleData.map(row => `
          <tr>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.siteName || '-'}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.single || 0}</td>
            <td style="padding: 10px; border: 1px solid #ddd;">${row.multiple || 0}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

/**
 * Format time from days to a readable format
 */
function formatTimeFromDays(days) {
  if (!days || days === 0) {
    return '0 Days';
  }

  const totalMinutes = Math.round(days * 24 * 60);
  
  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  } else if (totalMinutes < 1440) { // Less than 24 hours
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (minutes === 0) {
      return `${hours} hr`;
    }
    return `${hours} hr ${minutes} min`;
  } else { // 24 hours or more
    const totalDays = Math.floor(totalMinutes / 1440);
    const remainingHours = Math.floor((totalMinutes % 1440) / 60);
    
    if (remainingHours === 0) {
      return `${totalDays} Days`;
    }
    return `${totalDays} Days ${remainingHours} hr`;
  }
}

/**
 * Format label for display
 */
function formatLabel(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .replace(/_/g, ' ');
}